import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowLeft,
  Settings,
  Download,
  Plus,
  Users,
  FileImage,
  FileVideo,
  Eye,
  Edit3,
  Trash2,
  GitBranch,
  CheckCircle,
  AlertTriangle,
  XCircle,
  User,
  ChevronDown,
  RotateCcw,
  Target,
  TrendingUp,
  Award,
  BarChart3,
  MessageSquare,
  UserPlus,
  Activity,
  X,
  Upload,
  Loader2,
  RefreshCw,
  Shield,
  Gauge,
  PieChart,
  FolderPlus,
  UserMinus,
  FileText,
  Code
} from 'lucide-react';
import { ProjectService, ProjectWithMembers } from '../lib/projects';
import { ProjectMemberService, ProjectMemberInfo, AvailableTeamMember } from '../lib/project-members';
import { LimitEnforcementService } from '../lib/limit-enforcement-service';
import { CreativeService, CreativeWithAnalysis, getCreativeFamilyName } from '../lib/creatives';
import { ActivityService, ActivityWithUser } from '../lib/activity';
import { MessageService, MessageWithUser } from '../lib/messages';
import { MessageList, MessageInput } from '../components/ChatComponents';
import { ProjectSettings } from '../components/ProjectSettings';
import { ExportService } from '../lib/export-utils';
import { supabase } from '../lib/supabase';
import VersionHistory from '../components/VersionHistory';

// Chat system imports and types are now available



// Group creatives by their root creative (versions together)
const groupCreativesByFamily = (creatives: CreativeWithAnalysis[]) => {
  const families = new Map<string, {
    rootCreative: CreativeWithAnalysis;
    versions: CreativeWithAnalysis[];
    currentVersion: CreativeWithAnalysis;
    totalVersions: number;
  }>();

  creatives.forEach(creative => {
    // Determine the root ID (either the creative itself or its parent)
    const rootId = creative.parent_creative_id || creative.id;
    
    if (!families.has(rootId)) {
      // Find the root creative (the one without a parent_creative_id)
      const rootCreative = creatives.find(c => c.id === rootId) || creative;
      families.set(rootId, {
        rootCreative,
        versions: [],
        currentVersion: rootCreative,
        totalVersions: 0
      });
    }

    const family = families.get(rootId)!;
    family.versions.push(creative);
    
    // Update current version if this one is marked as current
    if (creative.is_current_version) {
      family.currentVersion = creative;
    }
  });

  // Calculate total versions and sort (newest first)
  families.forEach(family => {
    family.totalVersions = family.versions.length;
    family.versions.sort((a, b) => (b.version_number || 0) - (a.version_number || 0));
  });

  return Array.from(families.values());
};

// Calculate comprehensive project statistics from all creatives
const calculateProjectStatistics = (creatives: CreativeWithAnalysis[]) => {
  const analyzedCreatives = creatives.filter(c => c.analysis);
  
  console.log('🔍 ProjectStats Debug - Total creatives:', creatives.length);
  console.log('🔍 ProjectStats Debug - Analyzed creatives:', analyzedCreatives.length);
  
  if (analyzedCreatives.length === 0) {
    return {
      overall: {
        averageComplianceScore: 0,
        averagePerformanceScore: 0,
        totalIssues: 0,
        criticalIssues: 0,
        analyzedCreatives: 0,
        topPerformingPlatform: 'N/A',
        worstPerformingPlatform: 'N/A'
      },
      platforms: {},
      trends: {
        complianceImprovement: 0,
        performanceImprovement: 0,
        recentAnalyses: 0
      }
    };
  }

  // Calculate overall statistics using the stored overallScore values
  const totalComplianceScore = analyzedCreatives.reduce((sum, creative) => {
    if (!creative.analysis) return sum;
    
    // Use the stored overallScore instead of averaging platform scores
    const complianceScore = creative.analysis.overallScore || 0;
    console.log(`🔍 Creative "${creative.name}" compliance (overallScore):`, complianceScore);
    
    return sum + complianceScore;
  }, 0);

  const totalPerformanceScore = analyzedCreatives.reduce((sum, creative) => {
    if (!creative.analysis) return sum;
    
    // Use the stored overallPerformanceScore instead of averaging platform scores
    const performanceScore = creative.analysis.performanceAnalysis?.overallPerformanceScore || 0;
    console.log(`🔍 Creative "${creative.name}" performance (overallPerformanceScore):`, performanceScore);
    
    return sum + performanceScore;
  }, 0);

  console.log('🔍 Total compliance score (sum):', totalComplianceScore);
  console.log('🔍 Total performance score (sum):', totalPerformanceScore);
  console.log('🔍 Number of creatives to divide by:', analyzedCreatives.length);

  const allIssues = analyzedCreatives.flatMap(creative => 
    creative.analysis?.detectedIssues || []
  );

  const criticalIssues = allIssues.filter(issue => issue.severity === 'critical').length;

  // Calculate platform-specific statistics
  const platformStats: Record<string, {
    averageComplianceScore: number;
    averagePerformanceScore: number;
    totalCreatives: number;
    issuesCount: number;
    criticalIssuesCount: number;
  }> = {};

  const platformData: Record<string, {
    complianceScores: number[];
    performanceScores: number[];
    issues: number;
    criticalIssues: number;
  }> = {};

  analyzedCreatives.forEach(creative => {
    if (!creative.analysis) return;
    
    Object.entries(creative.analysis.platforms).forEach(([platform, data]) => {
      if (!platformData[platform]) {
        platformData[platform] = {
          complianceScores: [],
          performanceScores: [],
          issues: 0,
          criticalIssues: 0
        };
      }
      
      platformData[platform].complianceScores.push(data.complianceScore);
      platformData[platform].performanceScores.push(data.performanceScore);
      platformData[platform].issues += data.issues.length;
      platformData[platform].criticalIssues += data.issues.filter(i => i.severity === 'critical').length;
    });
  });

  // Convert to final platform stats
  Object.entries(platformData).forEach(([platform, data]) => {
    platformStats[platform] = {
      averageComplianceScore: data.complianceScores.reduce((a, b) => a + b, 0) / data.complianceScores.length,
      averagePerformanceScore: data.performanceScores.reduce((a, b) => a + b, 0) / data.performanceScores.length,
      totalCreatives: data.complianceScores.length,
      issuesCount: data.issues,
      criticalIssuesCount: data.criticalIssues
    };
  });

  // Find best and worst performing platforms
  const platformPerformances = Object.entries(platformStats).map(([platform, stats]) => ({
    platform,
    score: (stats.averageComplianceScore + stats.averagePerformanceScore) / 2
  }));

  platformPerformances.sort((a, b) => b.score - a.score);
  
  const topPerformingPlatform = platformPerformances[0]?.platform || 'N/A';
  const worstPerformingPlatform = platformPerformances[platformPerformances.length - 1]?.platform || 'N/A';

  // Calculate trends (simplified - would need historical data for real trends)
  const recentAnalyses = analyzedCreatives.filter(creative => {
    if (!creative.analysis) return false;
    const analysisDate = new Date(creative.analysis.timestamp);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return analysisDate > weekAgo;
  }).length;

  const finalComplianceScore = Math.round(totalComplianceScore / analyzedCreatives.length);
  const finalPerformanceScore = Math.round(totalPerformanceScore / analyzedCreatives.length);
  
  console.log('🔍 Final calculations:');
  console.log('🔍 - Average compliance:', totalComplianceScore / analyzedCreatives.length, '→ rounded:', finalComplianceScore);
  console.log('🔍 - Average performance:', totalPerformanceScore / analyzedCreatives.length, '→ rounded:', finalPerformanceScore);

  return {
    overall: {
      averageComplianceScore: finalComplianceScore,
      averagePerformanceScore: finalPerformanceScore,
      totalIssues: allIssues.length,
      criticalIssues,
      analyzedCreatives: analyzedCreatives.length,
      topPerformingPlatform,
      worstPerformingPlatform
    },
    platforms: platformStats,
    trends: {
      complianceImprovement: 0, // Would calculate from historical data
      performanceImprovement: 0, // Would calculate from historical data
      recentAnalyses
    }
  };
};

export function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [showProjectSettings, setShowProjectSettings] = useState(false);
  
  // Chat/messaging state
  const [messages, setMessages] = useState<MessageWithUser[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<MessageWithUser | null>(null);
  
  // Project data state
  const [project, setProject] = useState<ProjectWithMembers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  // Creatives data state
  const [creatives, setCreatives] = useState<CreativeWithAnalysis[]>([]);
  const [creativesLoading, setCreativesLoading] = useState(false);
  const [creativesError, setCreativesError] = useState<string | null>(null);
  
  // Activities data state
  const [activities, setActivities] = useState<ActivityWithUser[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState<string | null>(null);
  
  // Version history state
  const [selectedCreativeForVersions, setSelectedCreativeForVersions] = useState<string | null>(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Creative name editing state
  const [editingCreativeId, setEditingCreativeId] = useState<string | null>(null);
  const [editingCreativeName, setEditingCreativeName] = useState('');
  const [savingCreativeName, setSavingCreativeName] = useState(false);

  // Project statistics state
  const [projectStats, setProjectStats] = useState<{
    overall: {
      averageComplianceScore: number;
      averagePerformanceScore: number;
      totalIssues: number;
      criticalIssues: number;
      analyzedCreatives: number;
      topPerformingPlatform: string;
      worstPerformingPlatform: string;
    };
    platforms: Record<string, {
      averageComplianceScore: number;
      averagePerformanceScore: number;
      totalCreatives: number;
      issuesCount: number;
      criticalIssuesCount: number;
    }>;
    trends: {
      complianceImprovement: number;
      performanceImprovement: number;
      recentAnalyses: number;
    };
  } | null>(null);

  // Re-analyze state
  const [reanalyzingCreativeId, setReanalyzingCreativeId] = useState<string | null>(null);
  const [reanalyzeError, setReanalyzeError] = useState<string | null>(null);

  // Team members state
  const [projectMembers, setProjectMembers] = useState<ProjectMemberInfo[]>([]);
  const [availableTeamMembers, setAvailableTeamMembers] = useState<AvailableTeamMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [showAssignMemberModal, setShowAssignMemberModal] = useState(false);
  const [selectedMemberToAssign, setSelectedMemberToAssign] = useState<AvailableTeamMember | null>(null);
  const [userHasOrganization, setUserHasOrganization] = useState<boolean>(false);
  const [assigningMember, setAssigningMember] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  // Export state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Compute grouped creative families
  const creativeFamilies = React.useMemo(() => {
    return groupCreativesByFamily(creatives);
  }, [creatives]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showExportDropdown && !target.closest('.export-dropdown-container')) {
        setShowExportDropdown(false);
      }
    };

    if (showExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showExportDropdown]);

  // Load project data
  useEffect(() => {
    if (projectId) {
      loadProject();
      getCurrentUser();
    }
  }, [projectId]);

  // Load messages when collaboration tab is active
  useEffect(() => {
    if (activeTab === 'collaboration' && projectId && currentUserId) {
      loadMessages();
    }
  }, [activeTab, projectId, currentUserId]);

  // Load team members when component mounts or overview tab is active (team members are in sidebar)
  useEffect(() => {
    if (projectId && (activeTab === 'overview' || !activeTab)) {
      loadTeamMembers();
      if (userHasOrganization) {
        loadAvailableTeamMembers();
      }
    }
  }, [activeTab, projectId, userHasOrganization]);

  // Refresh activities when the window regains focus (user comes back from analysis page)
  useEffect(() => {
    const handleFocus = () => {
      if (projectId && !activitiesLoading) {
        loadActivities();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [projectId, activitiesLoading]);

  // Disable body scroll when modals are open
  useEffect(() => {
    const isModalOpen = showVersionHistory || showProjectSettings || showAssignMemberModal;
    
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showVersionHistory, showProjectSettings, showAssignMemberModal]);

  // Handle URL parameters for auto-opening version history and creative highlighting
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const showVersionHistoryParam = urlParams.get('showVersionHistory');
    const tabParam = urlParams.get('tab');
    const highlightParam = urlParams.get('highlight');
    
    if (showVersionHistoryParam) {
      setSelectedCreativeForVersions(showVersionHistoryParam);
      setShowVersionHistory(true);
      
      // Clean up URL parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('showVersionHistory');
      window.history.replaceState({}, '', newUrl.toString());
    }
    
    // Handle tab switching from URL parameters (e.g., from creative mentions)
    if (tabParam && ['overview', 'creatives', 'activity', 'collaboration'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    
    // Handle creative highlighting
    if (highlightParam && tabParam === 'creatives') {
      // Scroll to and highlight the creative after a delay to ensure content is loaded
      setTimeout(() => {
        const creativeElement = document.querySelector(`[data-creative-id="${highlightParam}"]`);
        
        if (creativeElement) {
          creativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a temporary highlight effect
          creativeElement.classList.add('creative-highlight');
          setTimeout(() => {
            creativeElement.classList.remove('creative-highlight');
          }, 3000);
        }
      }, 1000);
      
      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('tab');
      newUrl.searchParams.delete('highlight');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [location.search]);

  const loadProject = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      setError(null);
      const projectData = await ProjectService.getProject(projectId);
      setProject(projectData);
      
      // Also load creatives and activities for this project
      await Promise.all([
        loadCreatives(),
        loadActivities()
      ]);
    } catch (err) {
      console.error('Failed to load project:', err);
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const loadCreatives = async () => {
    if (!projectId) return;
    
    try {
      setCreativesLoading(true);
      setCreativesError(null);
      const creativesData = await CreativeService.getProjectCreatives(projectId);
      setCreatives(creativesData);
      
      // Calculate project statistics from creatives
      const stats = calculateProjectStatistics(creativesData);
      setProjectStats(stats);
    } catch (err) {
      console.error('Failed to load creatives:', err);
      setCreativesError(err instanceof Error ? err.message : 'Failed to load creatives');
    } finally {
      setCreativesLoading(false);
    }
  };

  const loadActivities = async () => {
    if (!projectId) return;
    
    try {
      setActivitiesLoading(true);
      setActivitiesError(null);
      const activitiesData = await ActivityService.getProjectActivities(projectId, 20);
      setActivities(activitiesData);
    } catch (err) {
      console.error('Failed to load activities:', err);
      setActivitiesError(err instanceof Error ? err.message : 'Failed to load activities');
    } finally {
      setActivitiesLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!project) return;
    
    try {
      setUpdating(true);
      const updateData = { status: newStatus };
      const updatedProject = await ProjectService.updateProject(project.id, updateData);
      
      // Log status change activity
      await ActivityService.logProjectStatusChanged(project.id, project.name, newStatus);
      
      setProject(updatedProject);
      
      // Reload activities to show the status change
      loadActivities();
    } catch (err) {
      console.error('Failed to update project status:', err);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-blue-600 bg-blue-100';
      case 'draft': return 'text-purple-600 bg-purple-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'paused': return 'text-yellow-600 bg-yellow-100';
      case 'approved': return 'text-emerald-600 bg-emerald-100';
      case 'warning': return 'text-amber-600 bg-amber-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'archived': return 'text-gray-600 bg-gray-100';
      case 'deleted': return 'text-slate-600 bg-slate-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };



  // Message loading and management functions
  const loadMessages = async () => {
    if (!projectId) return;
    
    try {
      setMessagesLoading(true);
      setMessagesError(null);
      
      // Check if user can access messages first
      const canAccess = await MessageService.canAccessProjectMessages(projectId);
      if (!canAccess) {
        setMessagesError('You do not have permission to access this project chat');
        return;
      }
      
      const messagesData = await MessageService.getRecentMessages(projectId);
      setMessages(messagesData);
    } catch (err) {
      console.error('Failed to load messages:', err);
      setMessagesError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const getCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);

      // Check if user has an organization
      if (user) {
        const { data: userData, error } = await supabase
          .from('users')
          .select('organization_id')
          .eq('id', user.id)
          .single();
        
        if (!error && userData) {
          setUserHasOrganization(!!userData.organization_id);
        }
      }
    } catch (error) {
      console.error('Failed to get current user:', error);
    }
  };

  const handleSendMessage = async (messageText: string) => {
    if (!projectId || !messageText.trim()) return;
    
    try {
      setSending(true);
      setMessagesError(null);
      
      const newMessage = await MessageService.sendMessage({
        projectId,
        messageText: messageText.trim(),
        replyToMessageId: replyTo?.id
      });
      
      // Add the new message to the list
      setMessages(prev => [...prev, newMessage]);
      
      // Clear reply state
      setReplyTo(null);
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessagesError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleEditMessage = async (messageId: string, newText: string) => {
    try {
      const updatedMessage = await MessageService.editMessage(messageId, newText);
      setMessages(prev => 
        prev.map(msg => msg.id === messageId ? updatedMessage : msg)
      );
    } catch (err) {
      console.error('Failed to edit message:', err);
      setMessagesError(err instanceof Error ? err.message : 'Failed to edit message');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await MessageService.deleteMessage(messageId);
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
    } catch (err) {
      console.error('Failed to delete message:', err);
      setMessagesError(err instanceof Error ? err.message : 'Failed to delete message');
    }
  };

  const handleReplyToMessage = (message: MessageWithUser) => {
    setReplyTo(message);
  };

  const handleCancelReply = () => {
    setReplyTo(null);
  };

  const handleProjectUpdated = (updatedProject: ProjectWithMembers) => {
    setProject(updatedProject);
    // Reload activities to show any project update activity
    loadActivities();
  };

  // Creative family name editing functions
  const handleStartEditingCreative = (creative: CreativeWithAnalysis) => {
    setEditingCreativeId(creative.id);
    setEditingCreativeName(getCreativeFamilyName(creative));
  };

  const handleCancelEditingCreative = () => {
    setEditingCreativeId(null);
    setEditingCreativeName('');
    setSavingCreativeName(false);
  };

  const handleSaveCreativeName = async () => {
    if (!editingCreativeId || !editingCreativeName.trim()) return;

    try {
      setSavingCreativeName(true);
      
      // Update the creative family name
      await CreativeService.renameCreativeFamily(editingCreativeId, editingCreativeName.trim());
      
      // Reload creatives to show the updated name
      await loadCreatives();
      
      // Reset editing state
      setEditingCreativeId(null);
      setEditingCreativeName('');
    } catch (err) {
      console.error('Failed to rename creative:', err);
      // You might want to show an error message here
    } finally {
      setSavingCreativeName(false);
    }
  };

  const handleCreativeNameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveCreativeName();
    } else if (e.key === 'Escape') {
      handleCancelEditingCreative();
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'creatives', label: 'Creatives', icon: FileImage },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'collaboration', label: 'Team Chat', icon: MessageSquare }
  ];

  // Team member management functions
  const loadTeamMembers = async () => {
    if (!projectId) return;
    
    try {
      setMembersLoading(true);
      setMembersError(null);
      
      // Get current project members from the project data
      const projectData = await ProjectService.getProject(projectId);
      if (projectData?.members) {
        // Transform to ProjectMemberInfo format
        const members: ProjectMemberInfo[] = projectData.members.map(member => ({
          id: member.id,
          userId: member.userId,
          projectId: projectId,
          role: member.role,
          permissions: member.permissions,
          assignedAt: member.joinedAt,
          assignedBy: member.invitedBy,
          user: member.user
        }));
        setProjectMembers(members);
      }
    } catch (err) {
      console.error('Failed to load team members:', err);
      setMembersError('Failed to load team members');
    } finally {
      setMembersLoading(false);
    }
  };

  const loadAvailableTeamMembers = async () => {
    if (!projectId) return;
    
    try {
      const available = await ProjectMemberService.getAvailableTeamMembers(projectId);
      setAvailableTeamMembers(available);
    } catch (err) {
      console.error('Failed to load available team members:', err);
    }
  };

  const handleAssignMember = async () => {
    if (!selectedMemberToAssign || !projectId) return;
    
    try {
      setAssigningMember(true);
      
      // Role will be inherited from the user's team_role automatically
      await ProjectMemberService.assignMemberToProject({
        projectId,
        userId: selectedMemberToAssign.id
      });
      
      // Refresh data
      const promises = [
        loadTeamMembers(),
        loadProject() // Refresh project to update member count
      ];
      
      if (userHasOrganization) {
        promises.push(loadAvailableTeamMembers());
      }
      
      await Promise.all(promises);
      
      // Reset form
      setShowAssignMemberModal(false);
      setSelectedMemberToAssign(null);
      
    } catch (err) {
      console.error('Failed to assign member:', err);
      setMembersError(err instanceof Error ? err.message : 'Failed to assign member');
    } finally {
      setAssigningMember(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!projectId) return;
    
    try {
      setRemovingMemberId(userId);
      
      await ProjectMemberService.removeMemberFromProject(projectId, userId);
      
      // Refresh data
      const promises = [
        loadTeamMembers(),
        loadProject() // Refresh project to update member count
      ];
      
      if (userHasOrganization) {
        promises.push(loadAvailableTeamMembers());
      }
      
      await Promise.all(promises);
      
    } catch (err) {
      console.error('Failed to remove member:', err);
      setMembersError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  // Handle project export
  const handleExport = async (format: 'pdf' | 'csv' | 'json') => {
    if (!project || !projectStats) {
      const errorMessage = 'Project data is not yet loaded. Please wait for the project to fully load before exporting.';
      setExportError(errorMessage);
      setTimeout(() => setExportError(null), 5000);
      return;
    }

    try {
      setExportLoading(true);
      setShowExportDropdown(false);
      setExportError(null);

      // Validate minimum data requirements
      if (!project.name || !project.id) {
        throw new Error('Invalid project data: missing required fields');
      }

      // Prepare project export data
      const exportData = {
        id: project.id,
        name: project.name,
        description: project.description || '',
        created_at: project.created_at,
        updated_at: project.updated_at,
        status: project.status,
        members: projectMembers || [],
        creativeFamilies: creativeFamilies || [],
        statistics: projectStats,
        totalCreatives: creatives?.length || 0,
        totalAnalyses: creatives?.filter(c => c.analysis)?.length || 0
      };

      // Export based on format
      switch (format) {
        case 'pdf':
          await ExportService.exportProjectToPDF(exportData);
          break;
        case 'csv':
          ExportService.exportProjectToCSV(exportData);
          break;
        case 'json':
          ExportService.exportProjectToJSON(exportData);
          break;
        default:
          throw new Error(`Unsupported export format: ${format}`);
      }

      // Clear any previous errors on successful export
      setExportError(null);

    } catch (error) {
      console.error(`Error exporting ${format.toUpperCase()}:`, error);
      const errorMessage = error instanceof Error 
        ? `Failed to export ${format.toUpperCase()}: ${error.message}`
        : `Failed to export ${format.toUpperCase()}. Please try again.`;
      
      setExportError(errorMessage);
      
      // Clear error after 5 seconds
      setTimeout(() => setExportError(null), 5000);
    } finally {
      setExportLoading(false);
    }
  };

  // Note: Project member roles are inherited from team roles and cannot be changed at project level
  // Users must update team roles in the Team page to change project permissions

  // Handle re-analyze creative
  const handleReanalyzeCreative = async (creativeId: string) => {
    if (!creativeId) return;
    
    try {
      setReanalyzingCreativeId(creativeId);
      setReanalyzeError(null);
      
      // First, check if user has enough credits
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setReanalyzeError('User not authenticated');
        return;
      }
      
      const creditCheck = await LimitEnforcementService.canPerformAnalysis(user.id);
      if (!creditCheck.allowed) {
        setReanalyzeError(creditCheck.reason || 'Insufficient credits for re-analysis');
        return;
      }
      
      // Show progress and perform re-analysis
      console.log('Starting re-analysis for creative:', creativeId);
      
      const analysisResult = await CreativeService.reanalyzeCreative(
        creativeId,
        undefined, // platforms (will analyze all by default)
        undefined, // campaignType
        undefined, // industry
        (progress) => {
          console.log('Re-analysis progress:', progress);
          // You can add a progress modal here if needed
        }
      );
      
      console.log('Re-analysis completed:', analysisResult);
      
      // Reload creatives to show updated analysis
      await loadCreatives();
      
      // Navigate to the analysis page to show results
      if (analysisResult?.analysisId) {
        navigate(`/dashboard/analysis?id=${analysisResult.analysisId}`);
      } else {
        // Fallback: navigate to creative analysis page
        navigate(`/dashboard/analysis?creativeId=${creativeId}`);
      }
      
    } catch (error) {
      console.error('Failed to re-analyze creative:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to re-analyze creative';
      setReanalyzeError(errorMessage);
      
      // Show error for a few seconds, then clear
      setTimeout(() => {
        setReanalyzeError(null);
      }, 5000);
    } finally {
      setReanalyzingCreativeId(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Project</h2>
          <p className="text-gray-600">Please wait while we fetch the project details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error ? 'Error Loading Project' : 'Project Not Found'}
          </h2>
          <p className="text-gray-600 mb-6">
            {error || 'The requested project could not be found.'}
          </p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/dashboard/projects')}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Back to Projects
            </button>
            {error && (
              <button
                onClick={loadProject}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 inline mr-2" />
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="bg-gray-50">
      <Helmet>
        <title>{project?.name ? `${project.name} - Rulevia` : 'Project Detail - Rulevia'}</title>
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => navigate('/dashboard/projects')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors min-h-[44px]"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm sm:text-base">Back to Projects</span>
              </button>
              
              <div className="h-6 w-px bg-gray-300 hidden sm:block" />
              
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                  <FileImage className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{project.name}</h1>
                  <p className="text-sm sm:text-base text-gray-600">{project.description || 'No description available'}</p>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-600">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status || 'active')}`}>
                      {project.status || 'active'}
                    </span>
                    <span>{creativeFamilies.length} creative{creativeFamilies.length !== 1 ? 's' : ''}</span>
                    <span>{creatives.length} version{creatives.length !== 1 ? 's' : ''}</span>
                    <span>{project.memberCount} member{project.memberCount !== 1 ? 's' : ''}</span>
                    <span>Updated {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : 'Unknown'}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate(`/dashboard/analysis?projectId=${project.id}`)}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all min-h-[44px] text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Add Creative</span>
              </button>
              
              <div className="relative">
                <select
                  value={project.status || 'active'}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updating}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-3 sm:px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 min-h-[44px] text-sm"
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="completed">Completed</option>
                  <option value="paused">Paused</option>
                  <option value="approved">Approved</option>
                  <option value="warning">Warning</option>
                  <option value="rejected">Rejected</option>
                  <option value="archived">Archived</option>
                  <option value="deleted">Deleted</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                {updating && (
                  <Loader2 className="absolute right-8 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-purple-600" />
                )}
              </div>
              
              <div className="relative export-dropdown-container">
                <button 
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                  disabled={exportLoading}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50"
                >
                  {exportLoading ? (
                    <Loader2 className="w-4 h-4 text-gray-600 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 text-gray-600" />
                  )}
                </button>
                
                {showExportDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {ExportService.getProjectExportOptions().map((option) => {
                      // Map icon names to components
                      const IconComponent = option.icon === 'FileText' ? FileText : 
                                           option.icon === 'BarChart3' ? BarChart3 : 
                                           option.icon === 'Code' ? Code : FileText;
                      
                      return (
                        <button
                          key={option.id}
                          onClick={() => handleExport(option.id as 'pdf' | 'csv' | 'json')}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg"
                          disabled={exportLoading}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <IconComponent className="w-4 h-4 text-purple-600" />
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 text-sm">{option.label}</div>
                              <div className="text-gray-600 text-xs">{option.description}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              <button 
                onClick={() => setShowProjectSettings(true)}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Project Settings"
              >
                <Settings className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Export Error Message */}
      {exportError && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-4 sm:mx-6 my-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{exportError}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setExportError(null)}
                  className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-3 sm:py-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap min-h-[44px] ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Main Overview */}
            <div className="lg:col-span-2 space-y-6 order-2 lg:order-1">
              {/* Enhanced Project Statistics */}
              <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Cumulative Project Analytics</h3>
                
                {projectStats ? (
                  <>
                    {/* Main Metrics */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
                      <div className="text-center">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                          <FileImage className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                        </div>
                        <div className="text-xl sm:text-2xl font-bold text-gray-900">{creativeFamilies.length}</div>
                        <div className="text-xs sm:text-sm text-gray-600">Total Creatives</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {creatives.length} versions total
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                          <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                        </div>
                        <div className="text-xl sm:text-2xl font-bold text-green-600">
                          {projectStats.overall.averageComplianceScore}%
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">Avg Compliance</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {projectStats.overall.criticalIssues} critical issues
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                          <Gauge className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                        </div>
                        <div className="text-xl sm:text-2xl font-bold text-purple-600">
                          {projectStats.overall.averagePerformanceScore}%
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">Avg Performance</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {projectStats.trends.recentAnalyses} this week
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                          <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                        </div>
                        <div className="text-xl sm:text-2xl font-bold text-red-600">
                          {projectStats.overall.totalIssues}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">Total Issues</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {projectStats.overall.criticalIssues} critical
                        </div>
                      </div>
                    </div>

                    {/* Compliance & Performance Overview */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                            <Shield className="w-4 h-4 text-green-600" />
                            <span>Compliance Score</span>
                          </h4>
                          <span className={`text-lg font-bold ${
                            projectStats.overall.averageComplianceScore >= 80 ? 'text-green-600' :
                            projectStats.overall.averageComplianceScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {projectStats.overall.averageComplianceScore}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-500 ${
                              projectStats.overall.averageComplianceScore >= 80 ? 'bg-green-500' :
                              projectStats.overall.averageComplianceScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${projectStats.overall.averageComplianceScore}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-600">
                          {projectStats.overall.averageComplianceScore >= 80 ? 'Excellent compliance across platforms' :
                           projectStats.overall.averageComplianceScore >= 60 ? 'Good compliance, minor issues to address' : 
                           'Multiple compliance issues need attention'}
                        </p>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                            <Gauge className="w-4 h-4 text-purple-600" />
                            <span>Performance Score</span>
                          </h4>
                          <span className={`text-lg font-bold ${
                            projectStats.overall.averagePerformanceScore >= 80 ? 'text-green-600' :
                            projectStats.overall.averagePerformanceScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {projectStats.overall.averagePerformanceScore}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-500 ${
                              projectStats.overall.averagePerformanceScore >= 80 ? 'bg-green-500' :
                              projectStats.overall.averagePerformanceScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${projectStats.overall.averagePerformanceScore}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-600">
                          {projectStats.overall.averagePerformanceScore >= 80 ? 'High performance potential detected' :
                           projectStats.overall.averagePerformanceScore >= 60 ? 'Good performance with room for improvement' : 
                           'Performance optimization recommended'}
                        </p>
                      </div>
                    </div>

                    {/* Platform Breakdown */}
                    {Object.keys(projectStats.platforms).length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-medium text-gray-900 mb-4 flex items-center space-x-2">
                          <PieChart className="w-4 h-4 text-blue-600" />
                          <span>Platform Performance Breakdown</span>
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                          {Object.entries(projectStats.platforms).map(([platform, stats]) => (
                            <div key={platform} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="font-medium text-gray-900 capitalize">{platform}</h5>
                                <span className="text-xs text-gray-600">
                                  {stats.totalCreatives} creative{stats.totalCreatives !== 1 ? 's' : ''}
                                </span>
                              </div>
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Compliance</span>
                                  <span className={`font-medium ${
                                    stats.averageComplianceScore >= 80 ? 'text-green-600' :
                                    stats.averageComplianceScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                                  }`}>
                                    {Math.round(stats.averageComplianceScore)}%
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Performance</span>
                                  <span className={`font-medium ${
                                    stats.averagePerformanceScore >= 80 ? 'text-green-600' :
                                    stats.averagePerformanceScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                                  }`}>
                                    {Math.round(stats.averagePerformanceScore)}%
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Issues</span>
                                  <span className="font-medium text-gray-900">
                                    {stats.issuesCount}
                                    {stats.criticalIssuesCount > 0 && (
                                      <span className="text-red-600 ml-1">
                                        ({stats.criticalIssuesCount} critical)
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top Insights */}
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                        <span>Key Insights</span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <Award className="w-4 h-4 text-green-600" />
                          <span className="text-gray-700">
                            Best Platform: <strong>{projectStats.overall.topPerformingPlatform}</strong>
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Target className="w-4 h-4 text-orange-600" />
                          <span className="text-gray-700">
                            Needs Attention: <strong>{projectStats.overall.worstPerformingPlatform}</strong>
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Activity className="w-4 h-4 text-blue-600" />
                          <span className="text-gray-700">
                            Recent Activity: <strong>{projectStats.trends.recentAnalyses} analyses this week</strong>
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4 text-purple-600" />
                          <span className="text-gray-700">
                            Team Size: <strong>{project.memberCount} member{project.memberCount !== 1 ? 's' : ''}</strong>
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <PieChart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No Analytics Available</h4>
                    <p className="text-gray-600 mb-4">
                      Upload and analyze creatives to see comprehensive project statistics.
                    </p>
                    <button
                      onClick={() => navigate(`/dashboard/analysis?projectId=${project.id}`)}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2 mx-auto"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Analyze First Creative</span>
                    </button>
                  </div>
                )}
              </div>



              {/* Creatives Preview or No Creatives State */}
              {creativeFamilies.length > 0 ? (
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Creatives</h3>
                    <button
                      onClick={() => setActiveTab('creatives')}
                      className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                    >
                      View all {creativeFamilies.length} creative{creativeFamilies.length !== 1 ? 's' : ''} →
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {creativeFamilies.slice(0, 6).map((family) => {
                      const creative = family.currentVersion;
                      return (
                      <div key={creative.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow" data-creative-id={creative.id}>
                        <div className="aspect-video bg-gray-100 relative">
                          {creative.type === 'image' ? (
                            <img
                              src={creative.url}
                              alt={creative.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=400';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FileVideo className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          {creative.analysis && (
                            <div className="absolute top-2 right-2">
                              <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                                Analyzed
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          {/* Editable Creative Name (smaller version for overview) */}
                          <div className="mb-1">
                            {editingCreativeId === family.rootCreative.id ? (
                              <div className="flex items-center space-x-1">
                                <input
                                  type="text"
                                  value={editingCreativeName}
                                  onChange={(e) => setEditingCreativeName(e.target.value)}
                                  onKeyDown={handleCreativeNameKeyPress}
                                  className="flex-1 text-xs font-medium text-gray-900 border border-purple-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                  autoFocus
                                  disabled={savingCreativeName}
                                  placeholder="Family name"
                                />
                                <button
                                  onClick={handleSaveCreativeName}
                                  disabled={savingCreativeName || !editingCreativeName.trim()}
                                  className="p-0.5 text-green-600 hover:text-green-700 disabled:opacity-50"
                                  title="Save"
                                >
                                  {savingCreativeName ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-3 h-3" />
                                  )}
                                </button>
                                <button
                                  onClick={handleCancelEditingCreative}
                                  disabled={savingCreativeName}
                                  className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                                  title="Cancel"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1 group">
                                <h4 className="font-medium text-gray-900 text-sm truncate flex-1">{getCreativeFamilyName(family.rootCreative)}</h4>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartEditingCreative(family.rootCreative);
                                  }}
                                  className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-gray-600 transition-opacity"
                                  title="Edit family name"
                                >
                                  <Edit3 className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            {creative.created_at ? new Date(creative.created_at).toLocaleDateString() : 'Unknown date'}
                          </p>
                          {family.totalVersions > 1 && (
                            <p className="text-xs text-blue-600 mt-1">
                              {family.totalVersions} versions
                            </p>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl p-8 border border-gray-200 text-center">
                  <FileImage className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Creatives Yet</h3>
                  <p className="text-gray-600 mb-6">Start by adding your first creative to analyze its compliance and performance.</p>
                  <button
                    onClick={() => navigate(`/dashboard/analysis?projectId=${project.id}`)}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2 mx-auto"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Add First Creative</span>
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6 order-1 lg:order-2">
              {/* Team Members */}
              <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Team Members</h3>
                  <div className="flex items-center space-x-2">
                    {userHasOrganization && availableTeamMembers.length > 0 && (
                      <button
                        onClick={() => setShowAssignMemberModal(true)}
                        className="text-purple-600 hover:text-purple-700 transition-colors"
                        title="Assign team member to project"
                      >
                        <UserPlus className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/dashboard/team')}
                      className="text-blue-600 hover:text-blue-700 transition-colors"
                      title="Invite new users to team"
                    >
                      <Users className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700">
                    Need to invite someone new? Go to{' '}
                    <button
                      onClick={() => navigate('/dashboard/team')}
                      className="underline hover:text-blue-800 font-medium"
                    >
                      Team page
                    </button>{' '}
                    to invite users to your organization.
                  </p>
                </div>

                {membersError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <XCircle className="w-4 h-4 text-red-500 mr-2" />
                      <span className="text-red-700 text-sm">{membersError}</span>
                    </div>
                  </div>
                )}

                {membersLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-600 mr-2" />
                    <span className="text-sm text-gray-600">Loading...</span>
                  </div>
                ) : (
                  <>
                    {projectMembers.length > 0 ? (
                      <div className="space-y-3">
                        {projectMembers.map((member) => (
                          <div key={member.userId} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                                <User className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {member.user.name || member.user.email}
                                </div>
                                <div className="text-xs text-gray-600 capitalize">
                                  {member.user.team_role || member.role || 'viewer'}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveMember(member.userId)}
                              disabled={removingMemberId === member.userId}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              title="Remove from project"
                            >
                              {removingMemberId === member.userId ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <UserMinus className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-3">No team members assigned</p>
                        {userHasOrganization && availableTeamMembers.length > 0 ? (
                          <button
                            onClick={() => setShowAssignMemberModal(true)}
                            className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                          >
                            Assign team members
                          </button>
                        ) : userHasOrganization ? (
                          <div className="space-y-2">
                            <p className="text-xs text-gray-500">No available team members</p>
                            <button
                              onClick={() => navigate('/dashboard/team')}
                              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                            >
                              Invite users to team
                            </button>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500">This is your personal project</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Project Info */}
              <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Project Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created</span>
                    <span className="text-gray-900">
                      {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Updated</span>
                    <span className="text-gray-900">
                      {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Owner</span>
                    <span className="text-gray-900">You</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status || 'active')}`}>
                      {project.status || 'active'}
                    </span>
                  </div>
                  {project.deadline && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deadline</span>
                      <div className="text-right">
                        <div className="text-gray-900">
                          {new Date(project.deadline).toLocaleDateString()}
                        </div>
                        <div className={`text-xs font-medium ${
                          new Date(project.deadline) < new Date() ? 'text-red-600' :
                          new Date(project.deadline) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {new Date(project.deadline) < new Date() ? 'Overdue' :
                           new Date(project.deadline) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) ? 'Due soon' :
                           'On track'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'creatives' && (
          <div className="space-y-6">
            {/* Header with Add Creative Button */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Project Creatives</h3>
                <p className="text-sm sm:text-base text-gray-600">Manage and analyze all creatives in this project</p>
              </div>
              <button
                onClick={() => navigate(`/dashboard/analysis?projectId=${project.id}`)}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 min-h-[44px] text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Add Creative</span>
              </button>
            </div>

            {/* Loading State */}
            {creativesLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-3 text-gray-600">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>Loading creatives...</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {creativesError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span>{creativesError}</span>
                </div>
                <button
                  onClick={loadCreatives}
                  className="text-red-600 hover:text-red-800"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Re-analyze Error State */}
            {reanalyzeError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span>{reanalyzeError}</span>
                </div>
                <button
                  onClick={() => setReanalyzeError(null)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Creatives Grid - Grouped by Creative Family */}
            {!creativesLoading && !creativesError && (
              <>
                {creativeFamilies.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {creativeFamilies.map((family) => {
                      const creative = family.currentVersion; // Show current version in the card
                      return (
                        <div key={creative.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow" data-creative-id={creative.id}>
                          {/* Creative Preview */}
                          <div className="aspect-video bg-gray-100 relative">
                            {creative.type === 'image' ? (
                              <img
                                src={creative.url}
                                alt={creative.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=400';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <FileVideo className="w-12 h-12 text-gray-400" />
                                <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                  Video
                                </div>
                              </div>
                            )}
                            
                            {/* Status Badges */}
                            <div className="absolute top-2 right-2 flex flex-col space-y-1">
                              {/* Version Badge */}
                              {(creative.version_number || creative.parent_creative_id) && (
                                <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                                  <GitBranch className="w-3 h-3" />
                                  <span>v{creative.version_number || 1}{creative.is_current_version && ' (Current)'}</span>
                                </div>
                              )}
                              
                              {/* Analysis Status Badge */}
                              {creative.analysis ? (
                                <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                                  <CheckCircle className="w-3 h-3" />
                                  <span>Analyzed</span>
                                </div>
                              ) : (
                                <div className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full">
                                  No Analysis
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Creative Info */}
                          <div className="p-3 sm:p-4">
                            {/* Editable Creative Name */}
                            <div className="mb-1">
                              {editingCreativeId === family.rootCreative.id ? (
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={editingCreativeName}
                                    onChange={(e) => setEditingCreativeName(e.target.value)}
                                    onKeyDown={handleCreativeNameKeyPress}
                                    className="flex-1 text-sm font-semibold text-gray-900 border border-purple-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    autoFocus
                                    disabled={savingCreativeName}
                                    placeholder="Family name"
                                  />
                                  <button
                                    onClick={handleSaveCreativeName}
                                    disabled={savingCreativeName || !editingCreativeName.trim()}
                                    className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                                    title="Save"
                                  >
                                    {savingCreativeName ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <CheckCircle className="w-4 h-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={handleCancelEditingCreative}
                                    disabled={savingCreativeName}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                                    title="Cancel"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2 group">
                                  <h4 className="font-semibold text-gray-900 truncate flex-1">{getCreativeFamilyName(family.rootCreative)}</h4>
                                  <button
                                    onClick={() => handleStartEditingCreative(family.rootCreative)}
                                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 transition-opacity"
                                    title="Edit family name"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-3">
                              Created {creative.created_at ? new Date(creative.created_at).toLocaleDateString() : 'Unknown date'}
                            </p>

                            {/* Analysis Summary */}
                            {creative.analysis && (
                              <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-gray-700">Analysis Results</span>
                                  <span className="text-xs text-gray-600">
                                    {Object.keys(creative.analysis.platforms || {}).length} platforms
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2 text-xs">
                                  {creative.analysis.overallScore >= 80 && (
                                    <span className="text-green-600 flex items-center space-x-1">
                                      <CheckCircle className="w-3 h-3" />
                                      <span>Good Score</span>
                                    </span>
                                  )}
                                  {creative.analysis.overallScore >= 60 && creative.analysis.overallScore < 80 && (
                                    <span className="text-yellow-600 flex items-center space-x-1">
                                      <AlertTriangle className="w-3 h-3" />
                                      <span>Needs Improvement</span>
                                    </span>
                                  )}
                                  {creative.analysis.overallScore < 60 && (
                                    <span className="text-red-600 flex items-center space-x-1">
                                      <XCircle className="w-3 h-3" />
                                      <span>Poor Score</span>
                                    </span>
                                  )}
                                  <span className="text-gray-600">
                                    Score: {creative.analysis.overallScore}/100
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex items-center space-x-1.5 sm:space-x-2">
                              <button
                                onClick={() => {
                                  if (creative.analysis?.analysisId) {
                                    navigate(`/dashboard/analysis?id=${creative.analysis.analysisId}`);
                                  } else {
                                    // If no analysis, redirect to analyze this creative
                                    navigate(`/dashboard/analysis?creativeId=${creative.id}`);
                                  }
                                }}
                                className="flex-1 bg-purple-600 text-white px-2 sm:px-3 py-2 rounded-lg hover:bg-purple-700 transition-colors text-xs sm:text-sm flex items-center justify-center space-x-1 min-h-[40px]"
                              >
                                <Eye className="w-4 h-4" />
                                <span>{creative.analysis ? 'View Analysis' : 'Analyze'}</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setSelectedCreativeForVersions(creative.id);
                                  setShowVersionHistory(true);
                                }}
                                className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center"
                                title="Version History"
                              >
                                <GitBranch className="w-4 h-4 text-gray-600" />
                              </button>
                              
                              {creative.analysis && (
                                <button
                                  onClick={() => handleReanalyzeCreative(creative.id)}
                                  disabled={reanalyzingCreativeId === creative.id}
                                  className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[40px] min-w-[40px] flex items-center justify-center"
                                  title="Reanalyze"
                                >
                                  {reanalyzingCreativeId === creative.id ? (
                                    <Loader2 className="w-4 h-4 text-gray-600 animate-spin" />
                                  ) : (
                                    <RotateCcw className="w-4 h-4 text-gray-600" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-8 border border-gray-200 text-center">
                    <FileImage className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Creatives</h3>
                    <p className="text-gray-600 mb-6">Upload and analyze your first creative to get started.</p>
                    <button
                      onClick={() => navigate(`/dashboard/analysis?projectId=${project.id}`)}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2 mx-auto"
                    >
                      <Upload className="w-5 h-5" />
                      <span>Upload Creative</span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Recent Activity</h3>
              <button
                onClick={loadActivities}
                disabled={activitiesLoading}
                className="flex items-center justify-center space-x-2 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 min-h-[40px] w-full sm:w-auto"
              >
                <RefreshCw className={`w-4 h-4 ${activitiesLoading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>

            {/* Loading State */}
            {activitiesLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-3 text-gray-600">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Loading activities...</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {activitiesError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span>{activitiesError}</span>
                </div>
                <button
                  onClick={loadActivities}
                  className="text-red-600 hover:text-red-800"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Activities List */}
            {!activitiesLoading && !activitiesError && (
              <>
                {activities.length > 0 ? (
                  <div className="space-y-4">
                    {activities.map((activity) => {
                      const IconComponent = (() => {
                        const iconName = ActivityService.getActivityIcon(activity.activity_type as any);
                        switch (iconName) {
                          case 'FolderPlus': return FolderPlus;
                          case 'Settings': return Settings;
                          case 'Upload': return Upload;
                          case 'BarChart3': return BarChart3;
                          case 'GitBranch': return GitBranch;
                          case 'UserPlus': return UserPlus;
                          case 'UserMinus': return UserMinus;
                          case 'Trash2': return Trash2;
                          default: return Activity;
                        }
                      })();

                      return (
                        <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${ActivityService.getActivityColor(activity.activity_type as any)}`}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900">
                              {ActivityService.formatActivityMessage(activity)}
                            </p>
                            <p className="text-xs text-gray-600">
                              {activity.created_at ? new Date(activity.created_at).toLocaleString() : 'Unknown time'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No Activity Yet</h4>
                    <p className="text-gray-600 mb-4">
                      Project activities will appear here as you and your team work on creatives.
                    </p>
                    <button
                      onClick={() => navigate(`/dashboard/analysis?projectId=${project.id}`)}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2 mx-auto"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add First Creative</span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'collaboration' && (
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Team Discussion</h3>
            
            <div className="space-y-4">
              <MessageList
                messages={messages}
                currentUserId={currentUserId || undefined}
                loading={messagesLoading}
                error={messagesError}
                onEdit={handleEditMessage}
                onDelete={handleDeleteMessage}
                onReply={handleReplyToMessage}
                projectId={project.id}
              />
              
              <MessageInput
                onSend={handleSendMessage}
                placeholder="Type a message to your team..."
                disabled={!currentUserId}
                sending={sending}
                replyTo={replyTo}
                onCancelReply={handleCancelReply}
                projectId={project.id}
              />
            </div>
          </div>
        )}


      </div>



      {/* Version History Modal */}
      <AnimatePresence>
        {showVersionHistory && selectedCreativeForVersions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-xl w-full max-w-4xl mx-4 max-h-[95vh] sm:max-h-[90vh] flex flex-col"
            >
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Creative Version History</h3>
                <button
                  onClick={() => {
                    setShowVersionHistory(false);
                    setSelectedCreativeForVersions(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                <VersionHistory
                  creativeId={selectedCreativeForVersions}
                  onVersionChange={() => {
                    // Reload creatives when version changes
                    loadCreatives();
                  }}
                  onCreateNewVersion={() => {
                    // Navigate to analysis page to create new version
                    if (selectedCreativeForVersions) {
                      // Close the version history modal first
                      setShowVersionHistory(false);
                      setSelectedCreativeForVersions(null);
                      
                      // Navigate to analysis page with creative ID and project ID for versioning
                      navigate(`/dashboard/analysis?projectId=${project.id}&versionOfCreative=${selectedCreativeForVersions}`);
                    }
                  }}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Project Settings Modal */}
      {project && (
        <ProjectSettings
          project={project}
          isOpen={showProjectSettings}
          onClose={() => setShowProjectSettings(false)}
          onProjectUpdated={handleProjectUpdated}
        />
      )}

      {/* Assign Member Modal */}
      <AnimatePresence>
        {showAssignMemberModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md mx-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Assign Team Member</h3>
                <button
                  onClick={() => {
                    setShowAssignMemberModal(false);
                    setSelectedMemberToAssign(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Team Member
                  </label>
                  <select
                    value={selectedMemberToAssign?.id || ''}
                    onChange={(e) => {
                      const member = availableTeamMembers.find(m => m.id === e.target.value);
                      setSelectedMemberToAssign(member || null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                  >
                    <option value="">Select a team member...</option>
                    {availableTeamMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} ({member.email})
                      </option>
                    ))}
                  </select>
                  
                  {availableTeamMembers.length === 0 && (
                    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">
                        No available team members to assign.
                      </p>
                      <button
                        onClick={() => {
                          setShowAssignMemberModal(false);
                          setSelectedMemberToAssign(null);
                          navigate('/dashboard/team');
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Go to Team page to invite users →
                      </button>
                    </div>
                  )}
                  
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      Don't see the person you're looking for?{' '}
                      <button
                        onClick={() => {
                          setShowAssignMemberModal(false);
                          setSelectedMemberToAssign(null);
                          navigate('/dashboard/team');
                        }}
                        className="underline hover:text-blue-800 font-medium"
                      >
                        Invite them to your team first
                      </button>
                    </p>
                  </div>
                </div>
                
                {selectedMemberToAssign && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Selected Member Role</h4>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium capitalize">
                        {selectedMemberToAssign.team_role || 'viewer'}
                      </span>
                      <span className="text-sm text-blue-700">
                        (Team Role - inherited for this project)
                      </span>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      Project permissions are inherited from team roles. To change permissions, update the team role in the Team page.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAssignMemberModal(false);
                    setSelectedMemberToAssign(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
                  disabled={assigningMember}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignMember}
                  disabled={!selectedMemberToAssign || assigningMember}
                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 min-h-[44px]"
                >
                  {assigningMember ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Assigning...</span>
                    </>
                  ) : (
                    <span>Assign to Project</span>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}